import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AllergyFormCompact } from './AllergyFormCompact';
import { Heart, CheckCircle2, AlertCircle, ChevronDown, ChevronUp, Eye, Edit3 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shown on the customer's booking card when an invitation is linked.
 * Lists every attending guest (RSVP=yes) and shows their allergy-form status.
 * Host can fill on behalf of any guest.
 */
export const GuestAllergyForms = ({ bookingId, customerName }) => {
  const [data, setData] = useState({ invitation: null, recipients: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [formTarget, setFormTarget] = useState(null);
  const [formExisting, setFormExisting] = useState(null);
  const [formReadOnly, setFormReadOnly] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/customer/bookings/${bookingId}/guest-allergy-forms`,
        { withCredentials: true },
      );
      setData(res.data || { invitation: null, recipients: [] });
    } catch {
      // Silent — booking may not have an invitation
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [bookingId]);

  // Hide entirely if there's no linked invitation
  if (loading) return null;
  if (!data.invitation) return null;

  const attending = (data.recipients || []).filter(
    (r) => (r.rsvp_response || '').toLowerCase() === 'yes',
  );
  if (attending.length === 0) return null;

  const pendingCount = attending.filter((r) => !r.allergy_form_completed).length;
  const completedCount = attending.length - pendingCount;

  const openForm = async (recipient, readOnly = false) => {
    setFormTarget(recipient);
    setFormReadOnly(readOnly);
    setFormExisting(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/customer/bookings/${bookingId}/guest-allergy-forms/${recipient.recipient_id}`,
        { withCredentials: true },
      );
      setFormExisting(res.data?.form || null);
    } catch {
      setFormExisting(null);
    }
  };

  const closeForm = () => {
    setFormTarget(null);
    setFormExisting(null);
    setFormReadOnly(false);
  };

  const submit = async (payload) => {
    if (!formTarget) return;
    try {
      await axios.post(
        `${API_URL}/api/customer/bookings/${bookingId}/guest-allergy-forms/${formTarget.recipient_id}`,
        payload,
        { withCredentials: true },
      );
      toast.success(`Form saved for ${formTarget.name || 'guest'}`);
      closeForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    }
  };

  return (
    <div
      className="px-6 py-3 bg-[#FFFDF8] border-t border-[#F0EDE6]"
      data-testid={`guest-allergy-section-${bookingId}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center justify-between w-full text-left"
        data-testid={`guest-allergy-toggle-${bookingId}`}
      >
        <div className="flex items-center gap-2">
          <Heart className="w-4 h-4 text-[#FF3B6B]" />
          <p className="text-sm font-semibold text-[#1E1E24]">
            Guest allergy & consent forms
          </p>
          {pendingCount > 0 ? (
            <Badge className="bg-[#FFEFD1] text-[#8A5A1F] border-none text-[10px]" data-testid={`guest-allergy-pending-badge-${bookingId}`}>
              {pendingCount} pending
            </Badge>
          ) : (
            <Badge className="bg-[#06D6A0]/15 text-[#06D6A0] border-none text-[10px]">
              All {completedCount} complete
            </Badge>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#6B6B7A]" /> : <ChevronDown className="w-4 h-4 text-[#6B6B7A]" />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-[#6B6B7A]">
            Please ensure a form is completed for each attending child. Guests can fill it themselves via the RSVP link in their email, or you can fill on their behalf.
          </p>
          <div className="divide-y divide-[#F0EDE6] rounded-xl border border-[#E8E5DF] bg-white overflow-hidden">
            {attending.map((r) => (
              <div
                key={r.recipient_id}
                className="flex items-center justify-between px-3 py-2.5"
                data-testid={`guest-row-${r.recipient_id}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1E1E24]">{r.name || r.email || 'Guest'}</span>
                  {r.allergy_form_completed ? (
                    <Badge className="bg-[#06D6A0]/15 text-[#06D6A0] border-none text-[10px]">
                      <CheckCircle2 className="w-3 h-3 mr-0.5" /> Completed
                    </Badge>
                  ) : (
                    <Badge className="bg-[#FFEFD1] text-[#8A5A1F] border-none text-[10px]">
                      <AlertCircle className="w-3 h-3 mr-0.5" /> Pending
                    </Badge>
                  )}
                  {r.allergy_filled_by && (
                    <span className="text-[10px] text-[#6B6B7A] uppercase">by {r.allergy_filled_by}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  {r.allergy_form_completed ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openForm(r, true)}
                        className="h-7 w-7 p-0"
                        title="View submitted form"
                        data-testid={`view-guest-form-${r.recipient_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openForm(r, false)}
                        className="h-7 w-7 p-0 text-[#6366F1]"
                        title="Edit form"
                        data-testid={`edit-guest-form-${r.recipient_id}`}
                      >
                        <Edit3 className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => openForm(r, false)}
                      className="h-7 bg-[#FF3B6B] hover:bg-[#E62E58] text-white text-xs rounded-full px-3"
                      data-testid={`fill-guest-form-${r.recipient_id}`}
                    >
                      Fill out form
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form dialog */}
      <Dialog open={!!formTarget} onOpenChange={(o) => !o && closeForm()}>
        <DialogContent className="bg-white max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-[#FF3B6B]" />
              Allergy & consent — {formTarget?.name || 'guest'}
            </DialogTitle>
            <DialogDescription>
              {formReadOnly ? 'Viewing the submitted form (read-only).' : "You're filling this form on behalf of your guest."}
            </DialogDescription>
          </DialogHeader>
          {formTarget && (
            <AllergyFormCompact
              initial={formExisting}
              attendeeName={formExisting?.attendee_name || formTarget.name || ''}
              defaultSignatureName={customerName || ''}
              readOnly={formReadOnly}
              onSubmit={submit}
              onCancel={closeForm}
              submitLabel={formExisting ? 'Save changes' : 'Submit form'}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GuestAllergyForms;
